import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthService from "../../../api/services/AuthService";
import { ProfileContext } from "../../../context/ProfileProvider";
import { DISABLE_ALL_NETWORK } from "../../../networkConfig";
import {
  formatNicknameForDisplay,
} from "../../../utils/nicknameUtils";
import {
  DEFAULT_AVATAR_SRC,
  pickAvatarPathFromResponse,
  pickProfilePictureFromUser,
  resolveAvatarImageUrl,
} from "../../../utils/avatarUtils";
import { ChangeNicknameModal, EditAvatarModal, AlertsSettingModal, NotificationLanguageModal } from "./SettingsModals";
import IdentityVerificationSection from "../../../customComponents/Didit/IdentityVerificationSection.jsx";
import "./SettingsPage.css";

const CARD_INTRO =
  "Choose Passkeys, Verification Code, or Trading Password to ensure the safety of your assets.";

function pickNickname(details) {
  if (!details) return "User";
  const candidates = [
    details.nickname,
    details.nickName,
    details.firstName,
    details.userName,
    details.username,
    details.name,
    details.fullName,
    details.full_name,
  ].filter(Boolean);
  if (candidates.length) return String(candidates[0]);

  const email = details.emailId || details.email;
  if (email && typeof email === "string") {
    const local = email.split("@")[0];
    if (local) return local;
  }

  const id = details.uuid || details.uid || details.userId || details.id;
  if (id) return `User-${String(id).slice(0, 8)}`;

  return "User";
}

const SettingsPage = () => {
  const navigate = useNavigate();
  const { userDetails, handleUserDetails, setUserDetails } = useContext(ProfileContext);
  const [nicknameOpen, setNicknameOpen] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);
  /** Remount avatar modal each time it opens → avoids leftover upload state / Strict Mode quirks. */
  const [avatarModalKey, setAvatarModalKey] = useState(0);
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [alertsTab, setAlertsTab] = useState("email");
  const [langOpen, setLangOpen] = useState(false);
  const [settingsTab, setSettingsTab] = useState("general");
  /** From GET `/v1/setting/get-nickname`; after load falls back to profile when empty. */
  const [settingsNicknameLoaded, setSettingsNicknameLoaded] = useState(false);
  const [settingsNickname, setSettingsNickname] = useState("");
  /** Relative path from GET `/v1/setting/get-avatar` (e.g. `uploads/...`). */
  const [settingsAvatarLoaded, setSettingsAvatarLoaded] = useState(false);
  const [settingsAvatarPath, setSettingsAvatarPath] = useState("");
  const [avatarCacheKey, setAvatarCacheKey] = useState(0);
  const [avatarFallbackStage, setAvatarFallbackStage] = useState(0);

  useEffect(() => {
    setAvatarFallbackStage(0);
  }, [settingsAvatarPath, userDetails, avatarCacheKey]);

  useEffect(() => {
    if (DISABLE_ALL_NETWORK) {
      setSettingsNicknameLoaded(true);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await AuthService.getNicknameSetting();
        if (cancelled) return;
        if (res?.success === false) {
          setSettingsNicknameLoaded(true);
          return;
        }
        const d = res?.data;
        const raw = d?.nickname ?? d?.data?.nickname;
        const n = raw != null ? String(raw).trim() : "";
        setSettingsNickname(n);
      } catch {
        if (!cancelled) setSettingsNickname("");
      } finally {
        if (!cancelled) setSettingsNicknameLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (DISABLE_ALL_NETWORK) {
      setSettingsAvatarLoaded(true);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await AuthService.getAvatarSetting();
        if (cancelled) return;
        if (res?.success === false) {
          setSettingsAvatarLoaded(true);
          return;
        }
        const path = pickAvatarPathFromResponse(res);
        setSettingsAvatarPath(path);
      } catch {
        if (!cancelled) setSettingsAvatarPath("");
      } finally {
        if (!cancelled) setSettingsAvatarLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const nickname = useMemo(() => {
    if (settingsNicknameLoaded && settingsNickname.trim()) {
      return formatNicknameForDisplay(settingsNickname.trim());
    }
    return formatNicknameForDisplay(pickNickname(userDetails));
  }, [settingsNicknameLoaded, settingsNickname, userDetails]);

  const avatarSrc = useMemo(() => {
    const fromUser = pickProfilePictureFromUser(userDetails);
    const settingsPath = settingsAvatarPath.trim();

    if (avatarFallbackStage >= 2) {
      return DEFAULT_AVATAR_SRC;
    }

    if (!settingsAvatarLoaded) {
      return resolveAvatarImageUrl(fromUser, avatarCacheKey);
    }

    if (avatarFallbackStage === 1 && fromUser) {
      return resolveAvatarImageUrl(fromUser, avatarCacheKey);
    }

    if (settingsPath) {
      return resolveAvatarImageUrl(settingsPath, avatarCacheKey);
    }

    return resolveAvatarImageUrl(fromUser, avatarCacheKey);
  }, [
    settingsAvatarLoaded,
    settingsAvatarPath,
    userDetails,
    avatarCacheKey,
    avatarFallbackStage,
  ]);

  const refreshProfile = () => handleUserDetails?.();

  const refreshSettingsAvatar = async () => {
    if (DISABLE_ALL_NETWORK) return;
    try {
      const res = await AuthService.getAvatarSetting();
      const path = pickAvatarPathFromResponse(res);
      if (path) {
        syncAvatarAfterSave(path);
        setUserDetails((prev) => ({
          ...prev,
          profilepicture: path,
          profilePicture: path,
          avatar: path,
        }));
      } else {
        setAvatarFallbackStage(0);
        setAvatarCacheKey((key) => key + 1);
      }
    } catch {
      setAvatarCacheKey((key) => key + 1);
    }
  };

  const syncNicknameAfterSave = (nextNickname) => {
    const n = nextNickname != null ? String(nextNickname).trim() : "";
    setSettingsNickname(n);
    setSettingsNicknameLoaded(true);
  };

  /** Relative path string from `/v1/setting/change-avatar`; pass `null` for offline/demo when only `profilepicture` was updated. */
  const syncAvatarAfterSave = (nextRelativePath) => {
    setAvatarFallbackStage(0);
    if (nextRelativePath == null) {
      setAvatarCacheKey((key) => key + 1);
      return;
    }
    const trimmed = String(nextRelativePath).trim();
    if (!trimmed || trimmed.startsWith("blob:")) {
      setAvatarCacheKey((key) => key + 1);
      return;
    }
    setSettingsAvatarPath(trimmed);
    setSettingsAvatarLoaded(true);
    setAvatarCacheKey((key) => key + 1);
  };

  return (
    <div className="dashboard_right">
      <div className="settings-page">
        <div className="settings-page__hero">
          <h1 className="settings-page__title">Settings</h1>
          <p className="settings-page__subtitle">Manage your preferences and account options.</p>
          <div className="settings-page__tabs" role="tablist" aria-label="Settings sections">
            <button
              type="button"
              id="settings-tab-general"
              role="tab"
              className={`settings-page__tab${settingsTab === "general" ? " is-active" : ""}`}
              aria-selected={settingsTab === "general"}
              onClick={() => setSettingsTab("general")}
            >
              General
            </button>
            <button
              type="button"
              id="settings-tab-identity"
              role="tab"
              className={`settings-page__tab${settingsTab === "identity" ? " is-active" : ""}`}
              aria-selected={settingsTab === "identity"}
              onClick={() => setSettingsTab("identity")}
            >
              Identity
            </button>
          </div>
        </div>

        {settingsTab === "identity" ? (
          <IdentityVerificationSection userDetails={userDetails} onAfterVerification={refreshProfile} />
        ) : null}

        {settingsTab === "general" ? (
          <>
            <section className="settings-page__card" aria-labelledby="settings-profile-heading">
              <div className="settings-page__card-head">
                <h2 id="settings-profile-heading" className="settings-page__card-title">
                  Profile
                </h2>
                <p className="settings-page__card-desc">{CARD_INTRO}</p>
              </div>

              <div className="settings-page__rows">
                <div className="settings-page__row">


                  <div className="d-flex profilebl_s">

                    <div className="settings-page__row-main">
                      <div className="settings-page__icon" aria-hidden="true">
                        <img src="/images/security/setting_profile_icon.svg" alt="Nickname" />
                      </div>
                      <div>
                        <div className="settings-page__row-title">Nickname</div>
                        <div className="settings-page__row-text">Customize your nickname in profile.</div>
                      </div>
                    </div>

                    <div className="settings-page__value">{nickname}</div>

                  </div>

                  <div className="settings-page__row-aside">

                    <button type="button" className="settings-page__pill" onClick={() => setNicknameOpen(true)}>
                      Change
                    </button>
                  </div>
                </div>

                <div className="settings-page__row border-none">

                  <div className="d-flex profilebl_s">

                    <div className="settings-page__row-main">
                      <div className="settings-page__icon" aria-hidden="true">
                        <img src="/images/security/setting_profile_icon2.svg" alt="Avatar" />
                      </div>
                      <div>
                        <div className="settings-page__row-title">Avatar</div>
                        <div className="settings-page__row-text">
                          Set an avatar to customize your account.
                        </div>
                      </div>
                    </div>

                    <div className="settings-page__avatar-wrap">
                      <img
                        key={`${settingsAvatarPath}-${avatarCacheKey}`}
                        className="settings-page__avatar"
                        src={avatarSrc}
                        alt=""
                        onError={() => setAvatarFallbackStage((stage) => stage + 1)}
                      />
                    </div>
                  </div>
                  <div className="settings-page__row-aside">
                    <button
                      type="button"
                      className="settings-page__pill"
                      onClick={() => {
                        setAvatarModalKey((k) => k + 1);
                        setAvatarOpen(true);
                      }}
                    >
                      Change
                    </button>
                  </div>
                </div>
              </div>
            </section>

            <section className="settings-page__card" aria-labelledby="settings-other-heading">
              <div className="settings-page__card-head">
                <h2 id="settings-other-heading" className="settings-page__card-title">
                  Other Settings
                </h2>
                <p className="settings-page__card-desc">{CARD_INTRO}</p>
              </div>

              <div className="settings-page__rows">
                <div className="settings-page__row">
                  <div className="settings-page__row-main">
                    <div className="settings-page__icon" aria-hidden="true">
                      <img src="/images/security/setting_profile_icon3.svg" alt="Notification Settings" />
                    </div>
                    <div>
                      <div className="settings-page__row-title">Notification Settings</div>
                      <div className="settings-page__row-text">
                        If there are any changes, you will be notified through SMS or email.
                      </div>
                    </div>
                  </div>
                  <div className="settings-page__row-aside">
                    <button
                      type="button"
                      className="settings-page__pill"
                      onClick={() => navigate("/user_profile/notification_settings")}
                    >
                      Manage
                    </button>
                  </div>
                </div>

                {/* <div className="settings-page__row">
              <div className="settings-page__row-main">
                <div className="settings-page__icon" aria-hidden="true">
                <img src="/images/security/setting_profile_icon4.svg" alt="Direct Message Management" />
                </div>
                <div>
                  <div className="settings-page__row-title">Direct Message Management</div>
                  <div className="settings-page__row-text">
                    Set how you want to receive push notifications.
                  </div>
                </div>
              </div>
              <div className="settings-page__row-aside">
                <button
                  type="button"
                  className="settings-page__pill"
                  onClick={() => {
                    setAlertsTab("message");
                    setAlertsOpen(true);
                  }}
                >
                  Manage
                </button>
              </div>
            </div> */}

                {/* <div className="settings-page__row">
              <div className="settings-page__row-main">
                <div className="settings-page__icon" aria-hidden="true">
                <img src="/images/security/setting_profile_icon5.svg" alt="Email Language Setting" />
                </div>
                <div>
                  <div className="settings-page__row-title">Email Language Setting</div>
                  <div className="settings-page__row-text">
                    This setting determines your email language preference.
                  </div>
                </div>
              </div>
              <div className="settings-page__row-aside">
                <button type="button" className="settings-page__pill" onClick={() => setLangOpen(true)}>
                  Manage
                </button>
              </div>
            </div> */}

                {/* <div className="settings-page__row settings-page__row--last">
              <div className="settings-page__row-main">
                <div className="settings-page__icon" aria-hidden="true">
                <img src="/images/security/setting_profile_icon6.svg" alt="Delivery Address" />
                </div>
                <div>
                  <div className="settings-page__row-title">Delivery Address</div>
                  <div className="settings-page__row-text">
                    Have physical items (gifts, artwork, hardware keys, etc.) delivered to your address.
                  </div>
                </div>
              </div>
              <div className="settings-page__row-aside">
                <button type="button" className="settings-page__pill">
                  Add
                </button>
              </div>
            </div> */}
              </div>
            </section>
          </>
        ) : null}
      </div>

      <ChangeNicknameModal
        open={nicknameOpen}
        onClose={() => setNicknameOpen(false)}
        initialNickname={nickname}
        onSaved={refreshProfile}
        onNicknameCommitted={(next) => {
          syncNicknameAfterSave(next);
        }}
        setUserDetails={setUserDetails}
      />
      <EditAvatarModal
        key={avatarModalKey}
        open={avatarOpen}
        onClose={() => setAvatarOpen(false)}
        currentAvatarUrl={avatarSrc}
        onSaved={refreshSettingsAvatar}
        onAvatarCommitted={(relativePath) => {
          syncAvatarAfterSave(relativePath);
        }}
        setUserDetails={setUserDetails}
      />
      <AlertsSettingModal open={alertsOpen} onClose={() => setAlertsOpen(false)} initialTab={alertsTab} />
      <NotificationLanguageModal open={langOpen} onClose={() => setLangOpen(false)} />
    </div>
  );
};

export default SettingsPage;
